import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FaFileContract, FaTimes } from 'react-icons/fa';
import { updateRental, updateAdminRental } from '../../Redux/Actions/actions';
import { RENTAL_TYPE } from '../../Views/RentalManagement/utils/rentalTypeHelpers';
import style from './EditRentalContractModal.module.css';

// Normaliza una fecha (ISO o Date) a formato YYYY-MM-DD para inputs date.
const toDateInput = (value) => (value ? String(value).slice(0, 10) : '');

// Convierte a número o devuelve undefined si está vacío.
const numOrUndef = (value) =>
  value === '' || value === null || value === undefined ? undefined : Number(value);

const emptyServices = { agua: false, gas: false, luz: false, internet: false, abl: false };

/**
 * Modal para editar la información de un contrato de alquiler (regular o admin).
 * Regular  -> PUT /rental/:id       (updateRental)
 * Admin    -> PUT /admin-rentals/:id (updateAdminRental)
 *
 * Props:
 * - contract: objeto fuente del contrato con todos los campos editables.
 *             Regular -> rawDetail.rental ; Admin -> rawDetail (flat).
 * - propertyTitle?: string
 * - type?: 'regular' | 'admin'
 * - onClose: () => void
 * - onSuccess?: (result) => void
 */
const EditRentalContractModal = ({
  contract = {},
  property = {},
  propertyTitle,
  type = RENTAL_TYPE.REGULAR,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const isAdmin = type === RENTAL_TYPE.ADMIN;
  const userId = useSelector((state) => state.userId);
  const userDetails = useSelector((state) => state.userDetails);

  const initialServices = useMemo(() => {
    const src = contract.servicesIncluded;
    if (src && typeof src === 'object' && !Array.isArray(src)) {
      return { ...emptyServices, ...src };
    }
    return { ...emptyServices };
  }, [contract.servicesIncluded]);

  const [form, setForm] = useState({
    // Datos económicos
    rentValue: contract.rentValue ?? '',
    currentRentValue: contract.currentRentValue ?? '',
    currency: contract.currency || 'ARS',
    adminFeePercent: contract.adminFeePercent ?? '',
    depositAmount: contract.depositAmount ?? contract.deposit ?? '',
    depositCurrency: contract.depositCurrency || 'ARS',
    // Fechas
    contractStart: toDateInput(contract.contractStart || contract.startDate),
    contractEnd: toDateInput(contract.contractEnd || contract.endDate),
    contractDuration: contract.contractDuration ?? '',
    paymentDueDay: contract.paymentDueDay ?? '',
    // Ajustes y mora
    adjustmentType: contract.adjustmentType || 'ICL',
    adjustmentFrequency: contract.adjustmentFrequency ?? '',
    adjustmentPercentage: contract.adjustmentPercentage ?? '',
    nextAdjustmentDate: toDateInput(contract.nextAdjustmentDate),
    lateInterestRate: contract.lateInterestRate ?? '',
    gracePeriodDays: contract.gracePeriodDays ?? '',
    // Expensas y servicios
    expensesIncluded: Boolean(contract.expensesIncluded),
    expensesAmount: contract.expensesAmount ?? '',
    // Propietario
    ownerName: contract.ownerName || '',
    ownerPhone: contract.ownerPhone || '',
    ownerEmail: contract.ownerEmail || '',
    ownerBankAccount: contract.ownerBankAccount || '',
    ownerDni: contract.ownerDni || '',
    ownerCuit: contract.ownerCuit || '',
    ownerBankAlias: contract.ownerBankAlias || '',
    // Propiedad embebida (solo admin)
    propertyAddress: contract.propertyAddress || '',
    propertyDepartment: contract.propertyDepartment || '',
    propertyProvince: contract.propertyProvince || '',
    propertyCountry: contract.propertyCountry || '',
    propertyType: contract.propertyType || '',
    propertyReference: contract.propertyReference || '',
    propertyNotes: contract.propertyNotes || '',
    // Observaciones y estado
    notes: contract.notes || '',
    specialClauses: contract.specialClauses || '',
    contractStatus: contract.contractStatus || 'active',
    isActive: contract.isActive ?? true,
  });
  const [services, setServices] = useState(initialServices);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const propertySummary = {
    address:
      form.propertyAddress ||
      property.address ||
      propertyTitle ||
      'No especificada',
    type: form.propertyType || property.type || 'No especificado',
    locality: form.propertyDepartment || property.locality || 'No especificada',
    province: form.propertyProvince || property.province || 'No especificada',
    country: form.propertyCountry || 'No especificado',
    reference: form.propertyReference || 'Sin referencia',
    environments: property.environments ?? '—',
    rooms: property.rooms ?? '—',
    bathrooms: property.bathrooms ?? '—',
    state: property.state || '—',
  };

  const handleChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: inputType === 'checkbox' ? checked : value,
    }));
  };

  const handleServiceChange = (e) => {
    const { name, checked } = e.target;
    setServices((prev) => ({ ...prev, [name]: checked }));
  };

  const validate = () => {
    if (form.rentValue !== '' && Number(form.rentValue) <= 0) {
      return 'El valor del alquiler debe ser mayor a 0.';
    }
    if (form.paymentDueDay !== '') {
      const d = Number(form.paymentDueDay);
      if (!Number.isInteger(d) || d < 1 || d > 31) {
        return 'El día de pago debe ser un entero entre 1 y 31.';
      }
    }
    if (form.adminFeePercent !== '') {
      const f = Number(form.adminFeePercent);
      if (!Number.isInteger(f) || f < 0 || f > 100) {
        return 'El canon de administración debe ser un entero entre 0 y 100.';
      }
    }
    if (form.contractStart && form.contractEnd) {
      if (new Date(form.contractEnd) < new Date(form.contractStart)) {
        return 'La fecha de finalización no puede ser anterior a la de inicio.';
      }
    }
    return '';
  };

  const buildPayload = () => {
    const payload = {
      rentValue: numOrUndef(form.rentValue),
      currentRentValue: numOrUndef(form.currentRentValue),
      currency: form.currency || undefined,
      adminFeePercent: numOrUndef(form.adminFeePercent),
      depositAmount: numOrUndef(form.depositAmount),
      depositCurrency: form.depositCurrency || undefined,
      contractStart: form.contractStart || undefined,
      contractEnd: form.contractEnd || undefined,
      contractDuration: numOrUndef(form.contractDuration),
      paymentDueDay: numOrUndef(form.paymentDueDay),
      adjustmentType: form.adjustmentType || undefined,
      adjustmentFrequency: numOrUndef(form.adjustmentFrequency),
      adjustmentPercentage:
        form.adjustmentPercentage === '' ? null : Number(form.adjustmentPercentage),
      nextAdjustmentDate: form.nextAdjustmentDate || undefined,
      lateInterestRate: numOrUndef(form.lateInterestRate),
      gracePeriodDays: numOrUndef(form.gracePeriodDays),
      expensesIncluded: form.expensesIncluded,
      expensesAmount: form.expensesIncluded ? numOrUndef(form.expensesAmount) : 0,
      servicesIncluded: services,
      ownerName: form.ownerName || undefined,
      ownerPhone: form.ownerPhone || undefined,
      ownerEmail: form.ownerEmail || undefined,
      ownerBankAccount: form.ownerBankAccount || undefined,
      ownerBankAlias: form.ownerBankAlias || undefined,
      notes: form.notes,
      specialClauses: form.specialClauses,
      contractStatus: form.contractStatus || undefined,
      isActive: form.isActive,
      performedBy: userId || undefined,
      performedByName: userDetails?.name || userDetails?.fullName || undefined,
    };

    // Campos exclusivos del contrato de solo administración.
    if (isAdmin) {
      payload.ownerDni = form.ownerDni || undefined;
      payload.ownerCuit = form.ownerCuit || undefined;
      payload.propertyAddress = form.propertyAddress || undefined;
      payload.propertyDepartment = form.propertyDepartment || undefined;
      payload.propertyProvince = form.propertyProvince || undefined;
      payload.propertyCountry = form.propertyCountry || undefined;
      payload.propertyType = form.propertyType || undefined;
      payload.propertyReference = form.propertyReference || undefined;
      payload.propertyNotes = form.propertyNotes || undefined;
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    try {
      setSubmitting(true);
      const payload = buildPayload();
      const result = await dispatch(
        isAdmin
          ? updateAdminRental(contract.id, payload)
          : updateRental(contract.id, payload)
      );
      toast.success('Contrato actualizado correctamente.');
      if (onSuccess) onSuccess(result);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'No se pudo actualizar el contrato.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={style.modal} onClick={onClose}>
      <div className={style.modalContent} onClick={(ev) => ev.stopPropagation()}>
        <div className={style.headerRow}>
          <h3 className={style.modalTitle}>
            <FaFileContract /> Editar contrato
          </h3>
          <button
            type="button"
            className={style.closeBtn}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <FaTimes />
          </button>
        </div>
        {propertyTitle && <p className={style.subtitle}>{propertyTitle}</p>}

        <form onSubmit={handleSubmit}>
          {/* Datos económicos */}
          <div className={style.sectionTitle}>Datos económicos</div>
          <div className={style.grid2}>
            <label className={style.label}>
              Valor inicial
              <input
                className={style.input}
                type="number"
                name="rentValue"
                min="0"
                value={form.rentValue}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              Valor actual
              <input
                className={style.input}
                type="number"
                name="currentRentValue"
                min="0"
                value={form.currentRentValue}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              Moneda
              <select
                className={style.input}
                name="currency"
                value={form.currency}
                onChange={handleChange}
              >
                <option value="ARS">ARS</option>
                <option value="USD">USD</option>
              </select>
            </label>
            <label className={style.label}>
              Canon de administración (%)
              <input
                className={style.input}
                type="number"
                name="adminFeePercent"
                min="0"
                max="100"
                step="1"
                value={form.adminFeePercent}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              Depósito
              <input
                className={style.input}
                type="number"
                name="depositAmount"
                min="0"
                value={form.depositAmount}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              Moneda del depósito
              <select
                className={style.input}
                name="depositCurrency"
                value={form.depositCurrency}
                onChange={handleChange}
              >
                <option value="ARS">ARS</option>
                <option value="USD">USD</option>
              </select>
            </label>
          </div>

          {/* Fechas */}
          <div className={style.sectionTitle}>Fechas del contrato</div>
          <div className={style.grid2}>
            <label className={style.label}>
              Inicio
              <input
                className={style.input}
                type="date"
                name="contractStart"
                value={form.contractStart}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              Finalización
              <input
                className={style.input}
                type="date"
                name="contractEnd"
                value={form.contractEnd}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              Duración (meses)
              <input
                className={style.input}
                type="number"
                name="contractDuration"
                min="0"
                value={form.contractDuration}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              Día de pago (1-31)
              <input
                className={style.input}
                type="number"
                name="paymentDueDay"
                min="1"
                max="31"
                value={form.paymentDueDay}
                onChange={handleChange}
              />
            </label>
          </div>

          {/* Ajustes y mora */}
          <div className={style.sectionTitle}>Ajustes y actualización de canon</div>
          <div className={style.grid2}>
            <label className={style.label}>
              Tipo de ajuste
              <select
                className={style.input}
                name="adjustmentType"
                value={form.adjustmentType}
                onChange={handleChange}
              >
                <option value="ICL">ICL</option>
                <option value="IPC">IPC</option>
                <option value="fixed">Porcentaje fijo</option>
                <option value="none">Sin ajuste</option>
              </select>
            </label>
            <label className={style.label}>
              Frecuencia de ajuste (meses)
              <input
                className={style.input}
                type="number"
                name="adjustmentFrequency"
                min="0"
                value={form.adjustmentFrequency}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              Porcentaje de ajuste (%)
              <input
                className={style.input}
                type="number"
                name="adjustmentPercentage"
                step="0.01"
                value={form.adjustmentPercentage}
                onChange={handleChange}
                placeholder="Solo para ajuste fijo"
              />
            </label>
            <label className={style.label}>
              Próximo ajuste
              <input
                className={style.input}
                type="date"
                name="nextAdjustmentDate"
                value={form.nextAdjustmentDate}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              Interés por mora (%)
              <input
                className={style.input}
                type="number"
                name="lateInterestRate"
                min="0"
                step="0.01"
                value={form.lateInterestRate}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              Días de gracia
              <input
                className={style.input}
                type="number"
                name="gracePeriodDays"
                min="0"
                value={form.gracePeriodDays}
                onChange={handleChange}
              />
            </label>
          </div>

          {/* Expensas y servicios */}
          <div className={style.sectionTitle}>Expensas y servicios</div>
          <label className={style.checkboxRow}>
            <input
              type="checkbox"
              name="expensesIncluded"
              checked={form.expensesIncluded}
              onChange={handleChange}
            />
            Expensas incluidas en el alquiler
          </label>
          {form.expensesIncluded && (
            <label className={style.label}>
              Monto de expensas
              <input
                className={style.input}
                type="number"
                name="expensesAmount"
                min="0"
                value={form.expensesAmount}
                onChange={handleChange}
              />
            </label>
          )}
          <div className={style.servicesRow}>
            {Object.keys(emptyServices).map((key) => (
              <label key={key} className={style.serviceItem}>
                <input
                  type="checkbox"
                  name={key}
                  checked={Boolean(services[key])}
                  onChange={handleServiceChange}
                />
                {key.toUpperCase()}
              </label>
            ))}
          </div>

          <div className={style.sectionTitle}>Información de la propiedad</div>
          <div className={style.quickGrid}>
            <div className={style.quickItem}>
              <strong>Dirección</strong>
              <span>{propertySummary.address}</span>
            </div>
            <div className={style.quickItem}>
              <strong>Tipo</strong>
              <span>{propertySummary.type}</span>
            </div>
            <div className={style.quickItem}>
              <strong>Localidad / Departamento</strong>
              <span>{propertySummary.locality}</span>
            </div>
            <div className={style.quickItem}>
              <strong>Provincia</strong>
              <span>{propertySummary.province}</span>
            </div>
            <div className={style.quickItem}>
              <strong>Ambientes / Dorm / Baños</strong>
              <span>
                {propertySummary.environments} / {propertySummary.rooms} / {propertySummary.bathrooms}
              </span>
            </div>
            <div className={style.quickItem}>
              <strong>Estado</strong>
              <span>{propertySummary.state}</span>
            </div>
          </div>

          {/* Propiedad embebida (solo admin) */}
          {isAdmin && (
            <>
              <div className={style.sectionTitle}>Editar datos de la propiedad (solo admin)</div>
              <div className={style.grid2}>
                <label className={style.label}>
                  Dirección
                  <input
                    className={style.input}
                    name="propertyAddress"
                    value={form.propertyAddress}
                    onChange={handleChange}
                  />
                </label>
                <label className={style.label}>
                  Localidad / Departamento
                  <input
                    className={style.input}
                    name="propertyDepartment"
                    value={form.propertyDepartment}
                    onChange={handleChange}
                  />
                </label>
                <label className={style.label}>
                  Provincia
                  <input
                    className={style.input}
                    name="propertyProvince"
                    value={form.propertyProvince}
                    onChange={handleChange}
                  />
                </label>
                <label className={style.label}>
                  País
                  <input
                    className={style.input}
                    name="propertyCountry"
                    value={form.propertyCountry}
                    onChange={handleChange}
                  />
                </label>
                <label className={style.label}>
                  Tipo de propiedad
                  <input
                    className={style.input}
                    name="propertyType"
                    value={form.propertyType}
                    onChange={handleChange}
                  />
                </label>
                <label className={style.label}>
                  Referencia
                  <input
                    className={style.input}
                    name="propertyReference"
                    value={form.propertyReference}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <label className={style.label}>
                Notas de la propiedad
                <textarea
                  className={style.textarea}
                  name="propertyNotes"
                  rows={2}
                  value={form.propertyNotes}
                  onChange={handleChange}
                />
              </label>
            </>
          )}

          {/* Propietario */}
          <div className={style.sectionTitle}>Propietario</div>
          <div className={style.grid2}>
            <label className={style.label}>
              Nombre
              <input
                className={style.input}
                name="ownerName"
                value={form.ownerName}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              Teléfono
              <input
                className={style.input}
                name="ownerPhone"
                value={form.ownerPhone}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              Email
              <input
                className={style.input}
                type="email"
                name="ownerEmail"
                value={form.ownerEmail}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              CBU / Cuenta bancaria
              <input
                className={style.input}
                name="ownerBankAccount"
                value={form.ownerBankAccount}
                onChange={handleChange}
              />
            </label>
            <label className={style.label}>
              Alias bancario
              <input
                className={style.input}
                name="ownerBankAlias"
                value={form.ownerBankAlias}
                onChange={handleChange}
              />
            </label>
            {isAdmin && (
              <>
                <label className={style.label}>
                  DNI
                  <input
                    className={style.input}
                    name="ownerDni"
                    value={form.ownerDni}
                    onChange={handleChange}
                  />
                </label>
                <label className={style.label}>
                  CUIT
                  <input
                    className={style.input}
                    name="ownerCuit"
                    value={form.ownerCuit}
                    onChange={handleChange}
                  />
                </label>
              </>
            )}
          </div>

          {/* Estado y observaciones */}
          <div className={style.sectionTitle}>Estado y observaciones</div>
          <div className={style.grid2}>
            <label className={style.label}>
              Estado del contrato
              <select
                className={style.input}
                name="contractStatus"
                value={form.contractStatus}
                onChange={handleChange}
              >
                <option value="active">Activo</option>
                <option value="pending">Pendiente</option>
                <option value="expired">Vencido</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </label>
            <label className={style.checkboxRow} style={{ alignSelf: 'end' }}>
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
              />
              Contrato activo
            </label>
          </div>
          <label className={style.label}>
            Observaciones
            <textarea
              className={style.textarea}
              name="notes"
              rows={2}
              value={form.notes}
              onChange={handleChange}
            />
          </label>
          <label className={style.label}>
            Cláusulas especiales
            <textarea
              className={style.textarea}
              name="specialClauses"
              rows={2}
              value={form.specialClauses}
              onChange={handleChange}
            />
          </label>

          {error && <div className={style.errorBox}>{error}</div>}

          <div className={style.actions}>
            <button
              type="button"
              className={style.cancelBtn}
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button type="submit" className={style.saveBtn} disabled={submitting}>
              {submitting ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRentalContractModal;
